import React, { PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import { ContentViewType } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { isDesktop, isMobile } from 'utils/contentViewType';
import { Paper, Box, Typography, Button, ButtonProps, PaperProps } from '@mui/material';

interface Props extends Omit<PaperProps, 'title'> {
  title?: ReactNode;
  subTitle?: string;
  isLoading?: boolean;
  actionBtn?: ReactNode;
  overTitleActionBtn?: ReactNode;
  containerClassName?: string;
  contentViewType?: ContentViewType;
  actionBtnProps?: Partial<ButtonProps> & { label?: string; isHidden?: boolean };
  overTitleActionBtnProps?: Partial<ButtonProps> & { label?: string; isHidden?: boolean };
}

export const CardContainer = ({
  title,
  subTitle,
  isLoading,
  children,
  actionBtn,
  actionBtnProps,
  overTitleActionBtn,
  overTitleActionBtnProps,
  containerClassName,
  contentViewType = ContentViewType.Desktop,
  ...paperProps
}: PropsWithChildren<Props>) => {
  const classes = useStyles();

  return (
    <Paper className={clsx(classes.container, containerClassName)} {...paperProps}>
      {(overTitleActionBtn || overTitleActionBtnProps) && (
        <Box>
          {overTitleActionBtn || (
            <>
              {overTitleActionBtnProps && !overTitleActionBtnProps?.isHidden && (
                <Button size="small" {...overTitleActionBtnProps}>
                  {overTitleActionBtnProps.label}
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {(title || actionBtn || actionBtnProps) && (
        <Box
          className={clsx(classes.header, {
            [classes.headerDesktop]: isDesktop(contentViewType),
            [classes.headerMobile]: isMobile(contentViewType),
          })}
        >
          <Typography component="span" variant="h6">
            {title}
          </Typography>

          {actionBtn || (
            <>
              {actionBtnProps && !actionBtnProps?.isHidden && (
                <Button size="small" {...actionBtnProps}>
                  {actionBtnProps.label}
                </Button>
              )}
            </>
          )}
        </Box>
      )}

      {subTitle && (
        <Typography component="span" variant="subtitle2" color="textSecondary">
          {subTitle}
        </Typography>
      )}

      {children}
    </Paper>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '16px',
    height: '100%',
    padding: '24px',
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerDesktop: {
    flexDirection: 'row',
  },
  headerMobile: {
    gap: '8px',
    flexDirection: 'column',
  },
});
